import React, { useId, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ImageLink } from "../utils/constant";
import { FileText, Plus, SquarePen, Trash2, Eye, Copy, Check } from "lucide-react";
import { createPortal } from "react-dom";
const isValidFileFormat = (editData) => {
  if (!editData) return "unknown";
  const imageMatch = [".jpg", ".png", ".webp", ".jpeg", ".svg"];
  const videoMatch = [".mp4", ".webm", ".mov", ".avi"];
  const pdfMatch = [".pdf"];
  const lowerData = editData.toLowerCase();
  if (pdfMatch.some((ext) => lowerData.endsWith(ext))) {
    return "pdf";
  } else if (videoMatch.some((ext) => lowerData.endsWith(ext))) {
    return "video";
  } else if (imageMatch.some((ext) => lowerData.endsWith(ext))) {
    return "image";
  } else {
    return "unknown";
  }
};

const ImageLightbox = ({ src, onClose }) =>
  createPortal(
    <div className="al-lightbox-overlay" onClick={onClose}>
      <button
        className="al-lightbox-close"
        onClick={onClose}
        aria-label="Close"
      >
        &#x2715;
      </button>
      <div className="al-lightbox-content" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt="Preview" className="al-lightbox-img" />
      </div>
    </div>,
    document.body,
  );

const ThumbWithLightbox = ({ fileUrl, isSvg, name }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <img
        src={fileUrl}
        alt={name}
        title="Click to preview"
        onClick={() => setOpen(true)}
        style={{
          height: isSvg ? "60px" : "100px",
          width: isSvg ? "60px" : "100px",
          objectFit: "cover",
          borderRadius: "2px",
          cursor: "zoom-in",
        }}
        className="lazy-load"
      />
      {open && <ImageLightbox src={fileUrl} onClose={() => setOpen(false)} />}
    </>
  );
};

const checkImage = (item, name) => {
  const formatdata = isValidFileFormat(item);
  const fileUrl = encodeURI(ImageLink(item));
  if (formatdata === "image") {
    const isSvg = item.toLowerCase().endsWith(".svg");
    return <ThumbWithLightbox fileUrl={fileUrl} isSvg={isSvg} name={name} />;
  }

  if (formatdata === "video") {
    return <p>Video</p>;
  }

  if (formatdata === "pdf") {
    return (
      <a
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="d-flex align-items-center justify-content-center"
        style={{
          height: "50px",
          width: "50px",
          textDecoration: "none",
          transition: "all 0.2s ease",
          cursor: "pointer",
        }}
      >
        <div className="text-center text-white">
          <FileText size={30} color="#a55353ff" />
          <div
            style={{
              fontSize: "10px",
              fontWeight: "600",
              marginTop: "5px",
              letterSpacing: "1px",
              color: "#8f5151ff",
            }}
          >
            PDF
          </div>
        </div>
      </a>
    );
  }

  return <p>-</p>;
};

const ToggleSwitch = ({ data, name, checked, handleStatusToggle, module }) => {
  const handleChange = () => {
    handleStatusToggle(data);
  };
  return (
    <div className="toggle-switch">
      <input
        type="checkbox"
        id={`toggleSwitch-${data?._id}-${name}`}
        checked={checked}
        onChange={handleChange}
      />
      <label
        htmlFor={`toggleSwitch-${data?._id}-${name}`}
        className="switch-label"
      >
        <span className="switch-inner" />
        <span className="switch-handle" />
      </label>
    </div>
  );
};

const ActionButton = ({
  data,
  handleEditClick,
  handleDelete,
  handleCopy,
  editicon = null,
  edittitle = null,
  module,
  type,
}) => {
  const hideActionTypes = [
    "ProductDetail",
    "ProjectDetail",
    "Product",
    "Project",
    "Home",
  ];

  const shouldHideActions = hideActionTypes.includes(type);
  const [copiedId, setCopiedId] = useState(null);

  const handleCopyClick = async (item) => {
    const success = await handleCopy(item);

    if (success) {
      setCopiedId(item._id);

      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    }
  };

  return (
    <div
      className="template-demo"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      {!shouldHideActions && (
        <>
          {/* Edit */}
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="edit-tooltip">
                {edittitle || "Edit"}
              </Tooltip>
            }
          >
            <div
              style={{
                background: "rgba(255, 108, 47, 0.1)",
                padding: "5px",
                borderRadius: "5px",
              }}
            >
              <SquarePen
                size={30}
                style={{
                  cursor: "pointer",
                  color: editicon
                    ? "rgba(99,221,99,1)"
                    : "rgb(38,94,215)",
                  padding: "5px",
                }}
                onClick={() => handleEditClick(data)}
              />
            </div>
          </OverlayTrigger>

          {handleCopy && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  {copiedId === data._id ? "Copied" : "Copy"}
                </Tooltip>
              }
            >
              <div
                style={{
                  background:
                    copiedId === data._id
                      ? "rgba(25,135,84,.1)"
                      : "rgba(13,110,253,.1)",
                  padding: "5px",
                  borderRadius: "5px",
                }}
              >
                {copiedId === data._id ? (
                  <Check
                    size={30}
                    color="#198754"
                    style={{ padding: "5px", cursor: "default" }}
                  />
                ) : (
                  <Copy
                    size={30}
                    color="#0d6efd"
                    style={{ padding: "5px", cursor: "pointer" }}
                    onClick={() => handleCopyClick(data)}
                  />
                )}
              </div>
            </OverlayTrigger>
          )}

          {/* Delete */}
          {module !== "emailTemplate" && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="delete-tooltip">Delete</Tooltip>}
            >
              <div
                style={{
                  background: "rgba(255,77,107,0.1)",
                  padding: "5px",
                  borderRadius: "5px",
                }}
              >
                <Trash2
                  size={30}
                  style={{
                    cursor: "pointer",
                    color: "rgba(255,77,107,1)",
                    padding: "5px",
                  }}
                  onClick={() => handleDelete(data)}
                />
              </div>
            </OverlayTrigger>
          )}
        </>
      )}
    </div>
  );
};
const PlusButton = ({ handleAdd, name = null, module, square = null }) => {
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id="plus-tooltip">Add {name}</Tooltip>}
    >
      <div
        onClick={handleAdd}
        style={{
          height: "40px",
          width: "40px",
          background: "#656CD9",
          borderRadius: square ? "10%" : "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "17px",
          color: "#fff",
          cursor: "pointer",
        }}
      >
        <Plus className="fa-solid fa-plus" />
      </div>
    </OverlayTrigger>
  );
};
const ViewButton = ({ handleRedirect, item }) => {
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={`view-tooltip-${item._id}`}>View</Tooltip>}
    >
      <div
        onClick={() => handleRedirect(item)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "40px",
          height: "40px",
          background: "rgba(255, 108, 47, 0.1)",
          borderRadius: "8px",
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255, 108, 47, 0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(255, 108, 47, 0.1)";
        }}
      >
        <Eye
          style={{
            color: "rgb(38, 94, 215)",
            width: "20px",
            height: "20px",
          }}
        />
      </div>
    </OverlayTrigger>
  );
};
const DeleteButton = ({ handleDelete, name = null, module }) => {
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id="plus-tooltip">Delete {name}</Tooltip>}
    >
      <div
        onClick={handleDelete}
        style={{
          height: "40px",
          width: "40px",
          background: "rgba(255, 77, 107, 1)",
          borderRadius: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "17px",
          color: "#fff",
          cursor: "pointer",
        }}
      >
        <Trash2 size={20} />
      </div>
    </OverlayTrigger>
  );
};
const DeleteSingleButton = ({ handleDelete, item }) => {
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={`view-tooltip-${item._id}`}>Delete</Tooltip>}
    >
      <div
        onClick={() => handleDelete(item)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "40px",
          height: "40px",
          background: "rgba(255, 77, 107, 0.1)",
          borderRadius: "8px",
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255, 108, 47, 0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(255, 108, 47, 0.1)";
        }}
      >
        <Trash2
          className="fa-solid fa-trash-can"
          size={30}
          style={{
            cursor: "pointer",
            color: "rgba(255, 77, 107, 1)",
            padding: "5px",
          }}
        />
      </div>
    </OverlayTrigger>
  );
};
const SwitchToggle = ({ checked, onChange, name, id, hideLabel = false }) => {
  const autoId = useId();
  const toggleId = id || `switch-${name || autoId}`;

  return (
    <div
      className={`d-flex align-items-center justify-content-between p-3 rounded ${hideLabel ? "" : "border border-dark"}`}
      style={{ gap: "1rem" }}
    >
      {!hideLabel && (
        <div>
          <p className="m-0 fw-semibold">isActive?</p>
          <small className="text-muted">
            {checked
              ? "Turning ON isActive mode will show this."
              : "Turning OFF isActive mode will hide this."}
          </small>
        </div>
      )}

      <div className="toggle-switch">
        <input
          type="checkbox"
          id={toggleId}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ display: "none" }}
        />
        <label
          htmlFor={toggleId}
          className="switch-label"
          style={{
            display: "inline-block",
            width: "45px",
            height: "24px",
            background: checked ? "#4caf50" : "#ccc",
            borderRadius: "50px",
            position: "relative",
            cursor: "pointer",
            transition: "background 0.3s",
          }}
        >
          <span
            className="switch-handle"
            style={{
              position: "absolute",
              top: "2px",
              left: checked ? "24px" : "2px",
              width: "20px",
              height: "20px",
              background: "#fff",
              borderRadius: "50%",
              transition: "left 0.3s",
              boxShadow: "0 0 3px rgba(0,0,0,0.3)",
            }}
          />
        </label>
      </div>
    </div>
  );
};
const Back = ({ handleBack }) => {
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id="plus-tooltip">Go Back</Tooltip>}
    >
      <div
        onClick={handleBack}
        style={{
          height: "40px",
          width: "40px",
          background: "linear-gradient(135deg, #101011ff, #8f4141ff)",
          borderRadius: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "17px",
          color: "#fff",
          cursor: "pointer",
        }}
      >
        <i class="fa-solid fa-outdent"></i>
      </div>
    </OverlayTrigger>
  );
};
const CopyButton = ({ onClick, name = "URL" }) => {
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id="copy-tooltip">Copy {name}</Tooltip>}>
      <div
        onClick={onClick}
        style={{
          height: "40px",
          width: "40px",
          background: "rgba(255, 108, 47, 0.1)",
          borderRadius: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "17px",
          color: "rgb(38, 94, 215)",
          cursor: "pointer",
        }}
      >
        <Copy size={20} style={{ color: "rgb(38, 94, 215)" }} />
      </div>
    </OverlayTrigger>
  );
};

export {
  checkImage,
  ToggleSwitch,
  ActionButton,
  PlusButton,
  DeleteButton,
  ViewButton,
  SwitchToggle,
  Back,
  isValidFileFormat,
  DeleteSingleButton,
  CopyButton
};
